import { getHasuraClaims } from "utils/jwt-utils";
import { entriesOf, normalizeClassifications, topOf } from "./classification";
import { getDsmProjectToken } from "./dsm-auth";
import { loadProjectDoc, saveProjectDoc, deleteProjectDoc } from "./dsm-remote";
import type { DsmScopeItem, ScopeFilter, ScopeItemInput } from "./types";

// Scope items live in the project's single backend record (read-modify-write
// over the whole `items` array).

// One project's scope items held in memory so we don't re-fetch the whole
// record on every list/filter. Keyed by projectId; a project switch reloads.
interface ProjectCache {
  docId: string | null;
  items: DsmScopeItem[];
}
let cache: { projectId: string; data: ProjectCache } | null = null;

async function ensureLoaded(projectId: string): Promise<ProjectCache> {
  if (cache && cache.projectId === projectId) return cache.data;
  const doc = await loadProjectDoc(projectId);
  const data: ProjectCache = {
    docId: doc?.id ?? null,
    items: doc?.items ?? []
  };
  cache = { projectId, data };
  return data;
}

// A DSM write is stamped to the project in the auth token, NOT the projectId
// we pass. If the bridge is holding another project's token — e.g. navigating
// from project A straight into the create-project flow, which then seeds
// project B — a write for B would silently land in A's record. Refuse instead
// of corrupting. (A null/absent token is left to authHeaders, which throws.)
function assertTokenScopedTo(projectId: string): void {
  const tokenProject =
    getHasuraClaims(getDsmProjectToken())?.["x-hasura-project-id"];
  if (tokenProject && tokenProject !== projectId) {
    throw new Error(
      `DSM: the active project token is scoped to ${tokenProject}, not ${projectId} — refusing to write to the wrong project's record`
    );
  }
}

async function persist(projectId: string, data: ProjectCache): Promise<void> {
  assertTokenScopedTo(projectId);
  // saveProjectDoc returns the record id (minted on first POST); keep it so
  // subsequent writes PUT the same record instead of creating duplicates.
  data.docId = await saveProjectDoc(data.items, data.docId);
}

/** Drop the in-memory copy (e.g. after an out-of-band change). */
export function invalidateScopeCache(): void {
  cache = null;
}

function applyFilter(
  rows: DsmScopeItem[],
  filter: ScopeFilter
): DsmScopeItem[] {
  const system = filter.topSystem ?? "UF";
  return rows
    .filter((r) => matchesFilter(r, filter))
    .sort(
      (a, b) =>
        topOf(a.classification, system).localeCompare(
          topOf(b.classification, system)
        ) || a.description.localeCompare(b.description)
    );
}

function commitmentsMatchFilter(item: DsmScopeItem, f: ScopeFilter): boolean {
  // When both are set, a SINGLE commitment must satisfy both — otherwise an
  // item committing this milestone under a different design package would
  // slip in, and a milestone modal could list more rows than its badge counts.
  if (f.designPackageId && f.milestoneId) {
    return item.commitments.some(
      (c) =>
        c.designPackageId === f.designPackageId &&
        c.milestoneId === f.milestoneId
    );
  }
  if (
    f.designPackageId &&
    !item.commitments.some((c) => c.designPackageId === f.designPackageId)
  ) {
    return false;
  }
  if (
    f.milestoneId &&
    !item.commitments.some((c) => c.milestoneId === f.milestoneId)
  ) {
    return false;
  }
  return true;
}

function textMatchesFilter(item: DsmScopeItem, text: string): boolean {
  const hay = [
    item.title ?? "",
    item.description,
    item.notes,
    item.responsibility,
    ...entriesOf(item.classification).map((c) => `${c.code} ${c.title}`),
    ...item.commitments.map((c) => c.notes),
    ...item.bids.map((b) => b.role)
  ]
    .join(" ")
    .toLowerCase();
  return hay.includes(text.toLowerCase());
}

function matchesFilter(item: DsmScopeItem, f: ScopeFilter): boolean {
  if (f.top && topOf(item.classification, f.topSystem ?? "UF") !== f.top) {
    return false;
  }
  if (!commitmentsMatchFilter(item, f)) return false;
  if (
    f.bidPackageId &&
    !item.bids.some((b) => b.bidPackageId === f.bidPackageId)
  ) {
    return false;
  }
  if (f.lod && !item.commitments.some((c) => c.lod === f.lod)) return false;
  if (f.text && !textMatchesFilter(item, f.text)) return false;
  return true;
}

/** Exactly one primary bid (mirrors the v3 prototype's save rule). */
function normalizeBids(bids: DsmScopeItem["bids"]): DsmScopeItem["bids"] {
  if (!bids.length) return bids;
  const primaryIndex = bids.findIndex((b) => b.primary);
  // No primary flagged: promote the first bid.
  if (primaryIndex === -1) {
    return bids.map((b, i) => (i === 0 ? { ...b, primary: true } : b));
  }
  // Exactly one primary already: leave as-is.
  if (bids.filter((b) => b.primary).length === 1) return bids;
  // Two or more flagged primary: keep the first flagged one, clear the rest.
  return bids.map((b, i) =>
    i === primaryIndex ? b : b.primary ? { ...b, primary: false } : b
  );
}

export const dsmApi = {
  async listScopeItems(
    projectId: string,
    filter: ScopeFilter = {}
  ): Promise<DsmScopeItem[]> {
    const { items } = await ensureLoaded(projectId);
    return applyFilter(items, filter);
  },

  async getScopeItem(
    projectId: string,
    id: string
  ): Promise<DsmScopeItem | undefined> {
    const { items } = await ensureLoaded(projectId);
    return items.find((r) => r.id === id);
  },

  async createScopeItem(
    projectId: string,
    input: ScopeItemInput
  ): Promise<DsmScopeItem> {
    const now = new Date().toISOString();
    const item: DsmScopeItem = {
      ...input,
      classification: normalizeClassifications(input.classification),
      bids: normalizeBids(input.bids),
      id: crypto.randomUUID(),
      projectId,
      createdAt: now,
      updatedAt: now
    };
    const data = await ensureLoaded(projectId);
    data.items = [...data.items, item];
    await persist(projectId, data);
    return item;
  },

  async updateScopeItem(
    projectId: string,
    id: string,
    patch: Partial<ScopeItemInput>
  ): Promise<DsmScopeItem | undefined> {
    const data = await ensureLoaded(projectId);
    const existing = data.items.find((r) => r.id === id);
    if (!existing) return undefined;
    const merged: DsmScopeItem = {
      ...existing,
      ...patch,
      classification:
        "classification" in patch
          ? normalizeClassifications(patch.classification)
          : existing.classification,
      bids: normalizeBids(patch.bids ?? existing.bids),
      updatedAt: new Date().toISOString()
    };
    data.items = data.items.map((r) => (r.id === id ? merged : r));
    await persist(projectId, data);
    return merged;
  },

  async deleteScopeItem(projectId: string, id: string): Promise<void> {
    const data = await ensureLoaded(projectId);
    if (!data.items.some((r) => r.id === id)) return;
    data.items = data.items.filter((r) => r.id !== id);
    await persist(projectId, data);
  },

  /**
   * Drop the project's entire scope-matrix record (not a single item). No-op
   * when no record exists yet.
   */
  async clearProjectScope(projectId: string): Promise<void> {
    assertTokenScopedTo(projectId);
    const data = await ensureLoaded(projectId);
    if (data.docId) await deleteProjectDoc(data.docId);
    invalidateScopeCache();
  }
};
